/**
 * Report header status chip — ONE compact chip in ReportHeaderStrip carries
 * the most important state the report page used to announce with banners.
 *
 * William, 2026-10-13: (1) remove the "Site SED updated … Regenerate" banner;
 * (2) while a new report generates, show "Generating report…" instead of
 * "Refresh required" + "Rerun"; (3) "Review quality details" moves into the
 * header; (4) "i dont want any banners on report pages".
 *
 * Every state reuses the derivation its banner used — nothing here
 * re-implements a rule:
 *   - delivery state ............ resolveDraftTierUx (DraftBanner's input)
 *   - narrative / conflicts /
 *     input drift / citations .. pickReportAlert (ReportAlertBanner's picker)
 *   - section notes ............. collectReportSectionNotices (the in-section
 *                                  quarantine / withheld / meta-leak notes)
 *   - providers ................. misconfiguredProviders (the removed ProviderMisconfiguredBanner)
 *   - truth questions, legacy simulation, PDF delivery, template staleness —
 *     the exact predicates SiteRagReportView already computed for its banners.
 *
 * The chip's `detail` is the former banner's text; the header shows it on
 * hover (title) and keyboard focus (aria-describedby).
 *
 * Pure: no React, no store. The generation state is selected by the caller
 * (`selectActiveReportGeneration`) because the header never reads the store.
 */
import type { SiteRagReport } from '../../../types/siteRag';
import { resolveDraftTierUx, type DraftTierUx } from '../../../lib/draftTierUx';
import { openAuditListNote } from '../../../lib/audit/openAuditFlag';
import { normalizeProgressPercent } from '../../../lib/coerceNumber';
import { pickReportAlert, type ReportAlertInputs, type ReportAlertKind } from './reportAlertSelection';
import {
  collectReportSectionNotices,
  describeSectionNotice,
  misconfiguredProviders,
  type ReportSectionNotice,
} from './reportSectionNotices';

export type ReportStatusChipKind =
  | 'generating'
  | 'invalid'
  | 'incomplete'
  | 'truth-questions'
  | 'legacy-simulation'
  | 'quality-hold'
  | 'audit-recommended'
  | 'draft'
  | 'narrative-unavailable'
  | 'section-withheld'
  | 'evidence-conflicts'
  | 'inputs-changed'
  | 'provider-not-configured'
  | 'pdf-unavailable'
  | 'citations-removed'
  | 'template-updated'
  | 'first-report';

/**
 * Highest first. The specified order is
 *   Generating > Invalid / Incomplete > Quality hold > Audit recommended >
 *   Draft > Section withheld > Inputs changed > Provider not configured >
 *   Citations removed > First report;
 * the states it did not name sit beside their nearest relative:
 *   - truth questions pre-empted the delivery/drift banners before (they
 *     replaced them outright), so they lead the audit family, after the
 *     artifact failures;
 *   - legacy simulation evidence blocks export, so it follows them;
 *   - degraded narrative is a delivery state, so it closes that family;
 *   - evidence conflicts, PDF delivery and template staleness keep
 *     ReportAlertBanner's relative order (conflicts above drift) and sit
 *     with the operational notices.
 */
export const REPORT_STATUS_CHIP_PRECEDENCE: readonly ReportStatusChipKind[] = [
  'generating',
  'invalid',
  'incomplete',
  'truth-questions',
  'legacy-simulation',
  'quality-hold',
  'audit-recommended',
  'draft',
  'narrative-unavailable',
  'section-withheld',
  'evidence-conflicts',
  'inputs-changed',
  'provider-not-configured',
  'pdf-unavailable',
  'citations-removed',
  'template-updated',
  'first-report',
];

export interface ReportStatusChip {
  kind: ReportStatusChipKind;
  /** Compact label rendered in the chip. */
  label: string;
  /** The former banner's text — hover (title) and focus (aria-describedby). */
  detail: string;
  tone: 'progress' | 'danger' | 'warning' | 'info';
  /** Generating only: 0–100, or null while waiting for site evidence. */
  progressPercent?: number | null;
}

/** An in-flight generation for this report's site + type. */
export interface ReportGenerationStatus {
  /** Run id — optional so callers can construct the status by hand. */
  runId?: string;
  progressPercent: number | null;
  waitingForSiteEvidence: boolean;
}

type ActiveGenerations = Record<string, {
  id: string;
  siteId: string;
  reportTypeId: string;
  startedAt: string;
  progress: number;
  waitingForSiteEvidence?: boolean;
}>;

/**
 * The newest in-flight run for the SAME site AND the SAME report type — so an
 * Update rerun (parentReportId) and an add-another sibling both count, while
 * other sites and other report types never do. A key exists in
 * `siteRag.activeGenerations` only while its run is in flight (terminal runs
 * move to `generationOutcomes`), so presence alone means "generating".
 * Mirrors FeasibilityRerunPanel's type-scoped reattach.
 */
export function selectActiveReportGeneration(
  activeGenerations: ActiveGenerations | null | undefined,
  siteId: string | null | undefined,
  reportType: string | null | undefined,
): ReportGenerationStatus | null {
  if (!activeGenerations || !siteId) return null;
  const typeId = reportType ?? 'feasibility';
  let latest: ActiveGenerations[string] | null = null;
  for (const run of Object.values(activeGenerations)) {
    if (!run || run.siteId !== siteId || run.reportTypeId !== typeId) continue;
    if (!latest || Date.parse(run.startedAt) > Date.parse(latest.startedAt)) latest = run;
  }
  if (!latest) return null;
  const waiting = latest.waitingForSiteEvidence === true;
  return {
    runId: latest.id,
    progressPercent: waiting ? null : normalizeProgressPercent(latest.progress),
    waitingForSiteEvidence: waiting,
  };
}

export interface ReportStatusInputs {
  /** The report actions act on (SiteRagReportView's `actionReport`): the
   *  delivery state and legacy-simulation check read it, as DraftBanner did. */
  report: SiteRagReport;
  /** The rendered projection (SiteRagReportView's `report`) — the alert and
   *  section-note detections read it, as ReportAlertBanner and the in-section
   *  notes did. Defaults to `report`. */
  renderedReport?: SiteRagReport;
  /** Live site (+ live SED) the drift and conflict checks compare against. */
  site?: ReportAlertInputs['site'];
  generation?: ReportGenerationStatus | null;
  truthQuestionCount?: number;
  requiresLegacySimulationRegeneration?: boolean;
  pdfDeliveryNotice?: string | null;
  /** Company admin — provider configuration and meta-leak notes are admin-only. */
  isAdmin?: boolean;
  templateStale?: boolean;
  firstRun?: boolean;
}

/** Findings the old DraftBanner listed under its title. */
const DETAIL_FINDING_LIMIT = 5;

function deliveryDetail(ux: DraftTierUx): string {
  const parts = [ux.bannerTitle, ux.bannerDetail].filter((part): part is string => Boolean(part));
  const shown = ux.findings.slice(0, DETAIL_FINDING_LIMIT);
  if (shown.length > 0) parts.push(shown.map(finding => `• ${finding}`).join('\n'));
  const note = openAuditListNote(ux.findingCount, shown.length);
  if (note) parts.push(note);
  return parts.join('\n');
}

function alertDetail(alert: { label: string; detail?: string }): string {
  return alert.detail ? `${alert.label}. ${alert.detail}` : alert.label;
}

const DRIFT_KINDS: ReadonlySet<ReportAlertKind> = new Set([
  'sed-drift', 'project-intent-drift', 'supply-chain-drift', 'financial-inputs-changed', 'pathway-comparison-stale',
]);

function sectionChip(notices: readonly ReportSectionNotice[]): ReportStatusChip {
  const sectionIds = new Set(notices.map(notice => notice.sectionId));
  const count = sectionIds.size;
  const allWithheld = notices.length > 0 && notices.every(notice => notice.kind === 'withheld');
  const label = allWithheld
    ? (count === 1 ? 'Section withheld' : `${count} sections withheld`)
    : (count === 1 ? 'Section quality note' : `${count} section quality notes`);
  return {
    kind: 'section-withheld',
    label,
    detail: [
      ...notices.map(describeSectionNotice),
      'Details and actions: ⋮ More → Section quality notes.',
    ].join('\n'),
    tone: 'warning',
  };
}

/**
 * The single highest-precedence state, or null when the report has nothing
 * to announce.
 */
export function resolveReportStatusChip(inputs: ReportStatusInputs): ReportStatusChip | null {
  const report = inputs.report;
  const rendered = inputs.renderedReport ?? report;

  // ── Generating report… ────────────────────────────────────────────────
  if (inputs.generation) {
    const { progressPercent, waitingForSiteEvidence } = inputs.generation;
    return {
      kind: 'generating',
      label: 'Generating report…',
      detail: waitingForSiteEvidence
        ? 'A new version of this report is queued and waiting for the site evidence build to finish; it starts automatically. The header returns to normal when it is done.'
        : `A new version of this report is being generated${progressPercent != null ? ` (${progressPercent}%)` : ''}. The header returns to normal when it finishes.`,
      tone: 'progress',
      progressPercent: waitingForSiteEvidence ? null : progressPercent,
    };
  }

  const ux = resolveDraftTierUx(report);
  const alert = pickReportAlert({ report: rendered, site: inputs.site, firstRun: inputs.firstRun });
  const notices = collectReportSectionNotices(rendered, { isAdmin: inputs.isAdmin });
  const providers = inputs.isAdmin ? misconfiguredProviders(rendered.providerStatuses) : [];
  const truthCount = inputs.truthQuestionCount ?? 0;

  const candidates: Partial<Record<ReportStatusChipKind, () => ReportStatusChip | null>> = {
    invalid: () => (ux.kind === 'invalid'
      ? { kind: 'invalid', label: 'Invalid', detail: deliveryDetail(ux), tone: 'danger' }
      : null),
    incomplete: () => (ux.kind === 'incomplete'
      ? { kind: 'incomplete', label: 'Incomplete', detail: deliveryDetail(ux), tone: 'danger' }
      : null),
    'truth-questions': () => (truthCount > 0
      ? {
        kind: 'truth-questions',
        label: `${truthCount} statement${truthCount === 1 ? '' : 's'} to confirm`,
        detail: `${truthCount} statement${truthCount === 1 ? '' : 's'} disagree${truthCount === 1 ? 's' : ''} with the site evidence. `
          + "Confirm what's correct and the answer is saved to the evidence record for this report and every future one.",
        tone: 'warning',
      }
      : null),
    'legacy-simulation': () => (inputs.requiresLegacySimulationRegeneration
      ? {
        kind: 'legacy-simulation',
        label: 'Legacy simulation retired',
        detail: 'Legacy simulation evidence retired. Historic references remain visible for audit only and were not mapped to '
          + 'simulation results. Rerun this report before delivery or export.',
        tone: 'danger',
      }
      : null),
    'quality-hold': () => {
      const storedTruthCount = Array.isArray(report.qaFindings?.truthQuestions)
        ? report.qaFindings.truthQuestions.length
        : 0;
      if (storedTruthCount > 0 && truthCount === 0) return null;
      return ux.kind === 'quality-hold'
        ? { kind: 'quality-hold', label: 'Quality hold', detail: deliveryDetail(ux), tone: 'warning' }
        : null;
    },
    'audit-recommended': () => (ux.kind === 'audit-recommended'
      ? { kind: 'audit-recommended', label: 'Audit recommended', detail: deliveryDetail(ux), tone: 'warning' }
      : null),
    draft: () => (ux.kind === 'legacy-draft'
      ? { kind: 'draft', label: 'Draft', detail: deliveryDetail(ux), tone: 'warning' }
      : null),
    'narrative-unavailable': () => (alert?.kind === 'degraded'
      ? { kind: 'narrative-unavailable', label: 'Narrative unavailable', detail: alertDetail(alert), tone: 'warning' }
      : null),
    'section-withheld': () => (notices.length > 0 ? sectionChip(notices) : null),
    'evidence-conflicts': () => (alert?.kind === 'high-conflicts'
      ? { kind: 'evidence-conflicts', label: 'Conflicts to review', detail: alertDetail(alert), tone: 'warning' }
      : null),
    'inputs-changed': () => (alert && DRIFT_KINDS.has(alert.kind)
      ? { kind: 'inputs-changed', label: 'Inputs changed', detail: alertDetail(alert), tone: 'warning' }
      : null),
    'provider-not-configured': () => (providers.length > 0
      ? {
        kind: 'provider-not-configured',
        label: 'Provider not configured',
        detail: [
          `Provider integration broken${providers.length > 1 ? ` (${providers.length} providers)` : ''} – admin attention required.`,
          ...providers.map(p => `• ${p.name}${p.error ? ` – ${p.error}` : ''}`),
          'Hidden from non-admin viewers. Check Cloud Run logs and rotate the affected secret / endpoint id.',
        ].join('\n'),
        tone: 'warning',
      }
      : null),
    'pdf-unavailable': () => (inputs.pdfDeliveryNotice
      ? { kind: 'pdf-unavailable', label: 'PDF unavailable', detail: inputs.pdfDeliveryNotice, tone: 'info' }
      : null),
    'citations-removed': () => (alert?.kind === 'citation-rewrite'
      ? { kind: 'citations-removed', label: 'Citations removed', detail: alertDetail(alert), tone: 'info' }
      : null),
    'template-updated': () => (inputs.templateStale
      ? { kind: 'template-updated', label: 'Template updated', detail: 'Template updated – layout refreshed from the current template.', tone: 'info' }
      : null),
    'first-report': () => (alert?.kind === 'first-run'
      ? { kind: 'first-report', label: 'First report', detail: alertDetail(alert), tone: 'info' }
      : null),
  };

  for (const kind of REPORT_STATUS_CHIP_PRECEDENCE) {
    const chip = candidates[kind]?.();
    if (chip) return chip;
  }
  return null;
}
